'use client'

import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent, TTrackingEcommerce } from '@/lib/gtm/type'
import { stringToSlug } from '@/lib/utils'
import { BreadCrumbItem, Option, Product } from '@/types/product'
import { sendGTMEvent } from '@next/third-parties/google'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface UseProductVariantsProps {
  product: Product
  initialColorSelected?: string | null
  updateWowBoxImages?: (option: Option) => void
}

interface UseProductVariantsReturn {
  colorOptions: Option[]
  colorSelected: string | null
  colorOptionActive: Option | null
  handleColorSelect: (color: string, option: Option) => void
}

const useProductVariants = ({
  product,
  initialColorSelected = null,
  updateWowBoxImages,
}: UseProductVariantsProps): UseProductVariantsReturn => {
  const [colorSelected, setColorSelected] = useState<string | null>(
    initialColorSelected
  )
  const [colorOptionActive, setColorOptionActive] = useState<Option | null>(
    null
  )
  const initialized = useRef(false)
  const trackEvent = useTrackEvent<Partial<TTrackingEcommerce>>()

  // Extract color options from product
  const colorOptions = useMemo(() => {
    const options =
      product?.options_value?.find((option) =>
        option?.options_id?.includes('color')
      )?.options || []

    // Filter out options without images and options that are out of stock
    return options.filter((option) => {
      const hasImages = option.image && option.image.length > 0;
      if (!hasImages) return false;

      // Check if this color has any available variants
      const colorSlug = stringToSlug(option.title);
      const hasAvailableVariant = product?.variants?.some(
        (variant) =>
          stringToSlug(variant.option1) === colorSlug &&
          variant.quantity > 0 &&
          variant.available
      );

      // If there are no variants, we can't determine stock, so we might show it.
      // But if there are variants, we must check for availability.
      if (product?.variants && product.variants.length > 0) {
        return hasAvailableVariant;
      }

      // Fallback for products with no variants array, just show if it has an image.
      return true;
    });
  }, [product])

  // Define handleColorSelect before using it in useEffect
  const handleColorSelect = useCallback(
    (color: string, option: Option) => {
      setColorSelected(color)
      setColorOptionActive(option)

      // If we need to update WowBox images based on selected color
      if (updateWowBoxImages) {
        updateWowBoxImages(option)
      }
    },
    [updateWowBoxImages]
  )

  // Initialize color selection from product data
  useEffect(() => {
    if (initialized.current || colorOptions.length === 0) return

    // If no color is selected, select the first one
    if (!colorSelected && colorOptions.length > 0) {
      const firstColor = colorOptions[0]
      const slug = stringToSlug(firstColor.title)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- ref-guarded one-time initialization from props, not a render-derived value
      handleColorSelect(slug, firstColor)
    }

    initialized.current = true
  }, [colorOptions, colorSelected, handleColorSelect])

  const convertBreadcrumbToCategory = useCallback(
    (bread_crumb: BreadCrumbItem[]) => {
      return bread_crumb.reduce(
        (acc, item, index) => {
          const key =
            index === 0 ? 'item_category' : `item_category${index + 1}`
          acc[key] = item.title
          return acc
        },
        {} as Record<string, string>
      )
    },
    []
  )

  const handleTracking = useCallback(() => {
    // Only track if product has required data
    if (!product?.id || !product?.title) return

    const data = {
      event: ETrackingEvent.ViewItem,
      ecommerce: {
        currency: 'VND',
        value: product.regular_price,
        items: [
          {
            item_id: product.id,
            item_name: product.title,
            price: product.regular_price,
            quantity: 1,
            item_brand: 'Coolmate',
            href: product.seo_alias,
            ...(product.bread_crumb
              ? convertBreadcrumbToCategory(product.bread_crumb)
              : {}),
            discount: product.percent,
            item_rating: product.review?.ratingValue,
            item_variant: colorSelected,
            item_url: product.seo_alias,
            item_image: product.icon_thumbnail?.src || product.images?.[0]?.src,
          },
        ],
      },
    }

    // clear ecommerce data trước khi track
    sendGTMEvent({ ecommerce: null })
    trackEvent(data)
  }, [product, colorSelected, convertBreadcrumbToCategory, trackEvent])

  // Tracking when color changes
  useEffect(() => {
    if (colorSelected && initialized.current) {
      handleTracking()
    }
  }, [colorSelected, handleTracking])

  return {
    colorOptions,
    colorSelected,
    colorOptionActive,
    handleColorSelect,
  }
}

export default useProductVariants
